import React, { useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { Layout } from "Components/Layout/Layout";
import { Footer } from "Components/Footer/Footer";
import { CourseCard } from "Components/CourseCard/CourseCard";
import { Tabs } from "Components/Tabs/Tabs";
import { Pagination } from "Components/Pagination/Pagination";
import { useMobile } from "Hooks/useMobile";
import { useFavorite } from "Hooks/useFavorite";
import { InformativeText } from "Components/InformativeText/InformativeText";
import { NotebookIcon } from "Components/Icons/Documents/NotebookIcon";
import { CourseCardSkeleton } from "Components/CourseCardSkeleton/CourseCardSkeleton";
import { RUTAS } from "rutas";
import { useNavigate } from "react-router-dom";
import { useCursosPaquetes } from "Hooks/useCursosPaquetes";
import { saveAs } from "file-saver";
import { useQuery } from "Hooks/useQuery";
import { paramService } from "Services/param_service";
import {
  TIPOS_APRENDIZAJE,
  DIPLOMA_FIRMA_URL,
  HTTP_METHODS,
  HTTP_HEADERS,
} from "Constantes/constants";
import "./MisCursos.scss";

const TABS = [{ label: "Asignados" }, { label: "Terminados" }];

export const MisCursos = () => {
  const { authUser } = useSelector((state) => state.auth);
  const isMobile = useMobile();
  const { cursos: allCursos, isLoading: isLoadingAllCursos } =
    useCursosPaquetes();
  const { toggleFavorite, isItemProcessing } = useFavorite(authUser.idUsuario);
  const [activeTab, setActiveTab] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingDiplomas, setDownloadingDiplomas] = useState(new Set());
  const itemsPerPage = isMobile ? 10 : 9;
  const isAssignedTab = activeTab === 1;
  const navigate = useNavigate();

  const { data: apiDiploma } = useQuery(paramService.param, {
    grupo: "apis",
  });

  const getParamValue = (data, codigo) => {
    if (!data || !Array.isArray(data)) return null;
    const param = data.find((p) => p.codigoParametro === codigo);
    return param?.parametro || null;
  };

  const endpointDiploma = getParamValue(apiDiploma?.data, "apiDiploma") || null;

  const isLoading = isLoadingAllCursos;

  const cursos = useMemo(() => {
    if (!allCursos) return [];

    return allCursos.filter((curso) => curso.asignado);
  }, [allCursos]);

  const coursesData = useMemo(() => {
    if (isAssignedTab) {
      return cursos.filter((course) => course.progreso.porcentajeTotal < 100);
    } else {
      return cursos.filter((course) => course.progreso.porcentajeTotal === 100);
    }
  }, [cursos, isAssignedTab]);

  const { paginatedData, totalPages } = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const slicedData = coursesData.slice(startIndex, endIndex);
    const total = Math.ceil(coursesData.length / itemsPerPage);

    return {
      paginatedData: slicedData,
      totalPages: total,
    };
  }, [coursesData, currentPage, itemsPerPage]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
  };

  const handleCardClick = (course) => {
    const path = RUTAS.CURSO(course.idCurso);
    navigate(path);
  };

  const handleDownloadDiploma = useCallback(
    async (course) => {
      if (!endpointDiploma) return;

      const courseId = course.idCurso;
      if (downloadingDiplomas.has(courseId)) return;

      setDownloadingDiplomas((prev) => new Set(prev).add(courseId));

      try {
        const diplomaData = {
          dpi: authUser?.usuario,
          idTipoAprendizaje: TIPOS_APRENDIZAJE.CURSO,
          idAprendizaje: courseId,
          firma: DIPLOMA_FIRMA_URL,
        };

        const response = await fetch(endpointDiploma, {
          method: HTTP_METHODS.POST,
          headers: {
            "Content-Type": HTTP_HEADERS.CONTENT_TYPE_JSON,
          },
          body: JSON.stringify(diplomaData),
        });

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();
        saveAs(blob, `DIPLOMA ${course.titulo}.pdf`);
      } catch (error) {
        console.error("Error descargando diploma:", error);
      } finally {
        setDownloadingDiplomas((prev) => {
          const newSet = new Set(prev);
          newSet.delete(courseId);
          return newSet;
        });
      }
    },
    [endpointDiploma, authUser, downloadingDiplomas]
  );

  return (
    <Layout>
      <div className="mis-cursos">
        <div className="mis-cursos__tabs-wrapper">
          <Tabs items={TABS} activeTab={activeTab} onChange={handleTabChange} />
          <h1 className="mis-cursos__title">
            Mis Cursos:&nbsp;
            {isLoading ? "00" : cursos.length.toString().padStart(2, "0")}/
            {isLoading
              ? "00"
              : (allCursos || []).length.toString().padStart(2, "0")}
          </h1>
        </div>

        <div className="mis-cursos__container">
          {!isLoading && coursesData.length === 0 && (
            <div className="mis-cursos__empty-state">
              <InformativeText>
                <NotebookIcon />
                {isAssignedTab
                  ? "Aún no tienes cursos asignados."
                  : "Aún no terminas un curso."}
              </InformativeText>
            </div>
          )}

          <div className="mis-cursos__grid">
            {!isLoading &&
              paginatedData.map((course) => {
                const isProcessing = isItemProcessing(course.idAlterno);
                const isDownloadingDiploma = downloadingDiplomas.has(
                  course.idCurso
                );

                return (
                  <CourseCard
                    key={course.idAlterno}
                    title={course.titulo}
                    imageUrl={course.img}
                    duration={course.duracion}
                    difficulty={course.dificultad}
                    language={course.idioma}
                    badgeText={course.categoria}
                    badgeTextColor={course.colorTexto}
                    badgeBackgroundColor={course.colorFondo}
                    badgeBorderColor={course.colorBorde}
                    isFavorite={Boolean(course.favorito)}
                    variant={isAssignedTab ? "progress" : "completed"}
                    isLoggedIn={true}
                    onToggleFavorite={() => toggleFavorite(course)}
                    onClick={() => handleCardClick(course)}
                    onViewAgain={() => handleCardClick(course)}
                    isProcessingFavorite={isProcessing}
                    progress={Math.trunc(course.progreso.porcentajeTotal)}
                    timeRemaining={Math.trunc(course.tiempoPendiente)}
                    onDownloadDiploma={() => {
                      handleDownloadDiploma(course);
                    }}
                    isDownloadingDiploma={isDownloadingDiploma}
                  />
                );
              })}

            {isLoading &&
              Array.from({ length: itemsPerPage }).map((_, index) => (
                <CourseCardSkeleton
                  key={index}
                  variant={isAssignedTab ? "progress" : "completed"}
                />
              ))}
          </div>
        </div>

        {coursesData.length > 0 && (
          <div className="mis-cursos__pagination-container">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
            />
          </div>
        )}
      </div>
      <Footer />
    </Layout>
  );
};
